import { AnimatePresence } from "framer-motion";
import {
  ArrowUpFromLineIcon,
  Delete,
  GripVertical,
  MinusIcon,
  Phone as PhoneIcon,
  SearchIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useCustomers } from "@/api/customer";
import type { Customer } from "@/api/types";
import { useDebounce } from "@/hooks/use-debounce";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuthStore } from "@/lib/auth";
import { cn, formatPhoneNumber, getCustomerName } from "@/lib/utils";
import { CustomerAvatar } from "@/modules/customer";
import { PhoneFilledIcon } from "@/ui/components/voice-icons";
import { CallDrawer } from "./call-drawer";
import { FloatingCard } from "./floating-card";
import { Keypad } from "./keypad";
import { useVoice } from "./voice-provider";

interface NewCallDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * When set, the card opens on a confirmation stage for this number
   * instead of the contacts/keypad dialer, so one-click call buttons
   * never dial without an explicit confirm.
   */
  pendingCall?: { to: string; name?: string; customer?: Partial<Customer> } | null;
}

type DialTab = "contacts" | "keypad";

/** Minimum digits before the dial button activates (US local numbers). */
const MIN_DIAL_DIGITS = 10;

function isTypingTarget(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

/**
 * Floating "new call" card. Matches the in-call widget treatment: draggable
 * with zone snapping, dark surface in both themes (standard convention for
 * call UIs). Rendered by VoiceProvider so it persists across pages.
 */
export function NewCallDialog({ open, onOpenChange, pendingCall }: NewCallDialogProps) {
  const gym = useAuthStore((state) => state.gym);
  const { status, startCall } = useVoice();
  const isMobile = useIsMobile();
  const [tab, setTab] = useState<DialTab>("contacts");
  const [search, setSearch] = useState("");
  const [dialed, setDialed] = useState("");
  const [minimized, setMinimized] = useState(false);
  const debouncedSearch = useDebounce(search.trim(), 300);

  // Reset transient state whenever the card closes.
  useEffect(() => {
    if (!open) {
      setTab("contacts");
      setSearch("");
      setDialed("");
      setMinimized(false);
    }
  }, [open]);

  const { data: customers, isLoading } = useCustomers(
    { gym: gym?.id, query: debouncedSearch, limit: 8 },
    { skip: !open || !gym?.id || !debouncedSearch, keepPreviousData: true },
  );

  const results = (customers ?? []).filter((customer) => customer.phoneNumber);
  const digitCount = dialed.replace(/[^\d]/g, "").length;
  const busy = status !== "idle";
  const canDial = !busy && digitCount >= MIN_DIAL_DIGITS;

  const call = (to: string, name?: string) => {
    onOpenChange(false);
    void startCall({ to, name });
  };

  // No overlay to click away, so support Escape to close. On the confirm
  // stage Enter places the call; on the keypad tab the physical keyboard
  // also dials: digits/*/# append, Backspace deletes, Enter places the
  // call. Keys typed into other inputs are left alone.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onOpenChange(false);
        return;
      }
      if (minimized || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;

      if (pendingCall) {
        if (event.key === "Enter" && !busy) {
          event.preventDefault();
          onOpenChange(false);
          void startCall(pendingCall);
        }
        return;
      }
      if (tab !== "keypad") return;

      if (/^[0-9*#]$/.test(event.key)) {
        event.preventDefault();
        setDialed((value) => value + event.key);
      } else if (event.key === "Backspace") {
        event.preventDefault();
        setDialed((value) => value.slice(0, -1));
      } else if (event.key === "Enter" && canDial) {
        event.preventDefault();
        onOpenChange(false);
        void startCall({ to: dialed });
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, minimized, tab, canDial, busy, dialed, pendingCall, onOpenChange, startCall]);

  // Prefer the real customer (photo when they have one); fall back to a
  // name-only stub so the avatar still renders initials for ad-hoc numbers.
  const confirmCustomer = pendingCall
    ? (pendingCall.customer ??
      (pendingCall.name
        ? {
            firstName: pendingCall.name.split(" ")[0],
            lastName: pendingCall.name.split(" ").slice(1).join(" "),
          }
        : undefined))
    : undefined;

  const confirmStage = pendingCall ? (
    <div className="mt-4 flex flex-col items-center pb-1">
      <CustomerAvatar
        customer={confirmCustomer}
        className="size-14 border-white/10 text-base"
        fallbackClassName="bg-none bg-white/10 text-zinc-50 [&_svg]:text-zinc-500"
      />
      <p className="mt-3 max-w-full truncate text-base font-semibold text-zinc-50">
        {pendingCall.name ?? formatPhoneNumber(pendingCall.to) ?? pendingCall.to}
      </p>
      {pendingCall.name ? (
        <p className="mt-0.5 text-sm text-zinc-400">
          {formatPhoneNumber(pendingCall.to) ?? pendingCall.to}
        </p>
      ) : null}
      <button
        type="button"
        disabled={busy}
        onClick={() => call(pendingCall.to, pendingCall.name)}
        className="mt-5 flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-green-500 text-sm font-medium text-white transition-colors hover:bg-green-400 disabled:pointer-events-none disabled:opacity-40"
      >
        <PhoneFilledIcon className="size-4" />
        Call
      </button>
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        className="mt-2 flex h-9 w-full items-center justify-center rounded-full text-sm font-medium text-zinc-400 transition-colors hover:bg-white/5 hover:text-zinc-50"
      >
        Cancel
      </button>
    </div>
  ) : null;

  const dialStage = !pendingCall ? (
    <>
      {/* Segmented tab control, styled to match the widget buttons */}
      <div
        className="mt-3 grid grid-cols-2 gap-1 rounded-lg bg-white/5 p-1"
        role="tablist"
        aria-label="Dial method"
      >
        {(["contacts", "keypad"] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={tab === value}
            onClick={() => setTab(value)}
            className={cn(
              "rounded-md py-1 text-xs font-medium capitalize transition-colors",
              tab === value
                ? "bg-white/15 text-zinc-50"
                : "text-zinc-400 hover:bg-white/5 hover:text-zinc-200",
            )}
          >
            {value}
          </button>
        ))}
      </div>

      {tab === "contacts" ? (
        <div className="mt-3">
          <div className="relative">
            <SearchIcon className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-zinc-500" />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by name or phone number..."
              className="h-9 w-full rounded-lg border border-white/10 bg-white/5 pl-8 pr-3 text-sm text-zinc-50 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-white/20"
            />
          </div>
          {/* Fixed height so typing/loading/results never resize the card */}
          <div className="mt-2 h-[264px] overflow-y-auto">
            {!debouncedSearch ? (
              <p className="flex h-full items-center justify-center px-1 text-sm text-zinc-500">
                Search for a contact to call.
              </p>
            ) : results.length > 0 ? (
              <ul className="divide-y divide-white/5">
                {results.map((customer: Customer) => (
                  <li key={customer.id}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => call(customer.phoneNumber, getCustomerName(customer))}
                      className="flex w-full items-center gap-3 rounded-md px-2 py-2.5 text-left transition-colors hover:bg-white/5 disabled:opacity-50"
                    >
                      <CustomerAvatar customer={customer} />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium text-zinc-50">
                          {getCustomerName(customer)}
                        </span>
                        <span className="block truncate text-xs text-zinc-400">
                          {formatPhoneNumber(customer.phoneNumber)}
                        </span>
                      </span>
                      <PhoneIcon className="size-4 shrink-0 text-zinc-400" />
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="flex h-full items-center justify-center px-1 text-sm text-zinc-500">
                {isLoading ? "Searching..." : "No contacts with a phone number found."}
              </p>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3">
          {/* Symmetric side columns keep the number centered and stop the
                  delete button from shifting the layout as it appears. */}
          <div className="grid h-12 grid-cols-[2rem_1fr_2rem] items-center">
            <span aria-hidden />
            <span
              className="truncate text-center text-xl font-medium tracking-wide text-zinc-50"
              aria-live="polite"
              aria-label="Number to dial"
            >
              {dialed ? (formatPhoneNumber(dialed) ?? dialed) : ""}
            </span>
            <button
              type="button"
              onClick={() => setDialed((value) => value.slice(0, -1))}
              aria-label="Delete last digit"
              tabIndex={dialed ? 0 : -1}
              className={cn(
                "justify-self-end text-zinc-400 transition-colors hover:text-zinc-50",
                !dialed && "invisible",
              )}
            >
              <Delete className="size-5" />
            </button>
          </div>
          <Keypad onDigit={(digit) => setDialed((value) => value + digit)} />
          <button
            type="button"
            disabled={!canDial}
            onClick={() => call(dialed)}
            className="mt-3 flex h-11 w-full items-center justify-center gap-1.5 rounded-full bg-green-500 text-sm font-medium text-white transition-colors hover:bg-green-400 disabled:pointer-events-none disabled:opacity-40"
          >
            <PhoneFilledIcon className="size-4" />
            Call
          </button>
        </div>
      )}
    </>
  ) : null;

  // Mobile: bottom drawer pinned above all content; drag down docks it to
  // its header bar (the X is the only way to close). Desktop: the draggable
  // floating card with zone snapping.
  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <CallDrawer
            aria-label="New call"
            header={<p className="text-sm font-semibold">New call</p>}
            minimized={minimized}
            onMinimizedChange={setMinimized}
            onClose={() => onOpenChange(false)}
          >
            {confirmStage}
            {dialStage}
          </CallDrawer>
        )}
      </AnimatePresence>
    );
  }

  return (
    <AnimatePresence>
      {open && (
        <FloatingCard
          aria-label="New call"
          initialZone="center"
          className="w-80 rounded-2xl border border-white/10 bg-zinc-950/95 p-4 text-zinc-50 shadow-2xl backdrop-blur"
        >
          {({ zone, snapTo }) => (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <GripVertical aria-hidden className="size-4 shrink-0 text-zinc-500" />
                  <p className="text-sm font-semibold">New call</p>
                </div>
                <div className="flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      if (minimized) {
                        setMinimized(false);
                      } else {
                        setMinimized(true);
                        // Bottom-left already reads as "tucked away"; every
                        // other zone collapses to bottom-right.
                        snapTo(zone === "bottom-left" ? "bottom-left" : "bottom-right");
                      }
                    }}
                    aria-label={minimized ? "Expand" : "Minimize"}
                    title={minimized ? "Expand" : "Minimize"}
                    className="flex size-7 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-50"
                  >
                    {minimized ? (
                      <ArrowUpFromLineIcon className="size-4" />
                    ) : (
                      <MinusIcon className="size-4" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => onOpenChange(false)}
                    aria-label="Close"
                    className="flex size-7 items-center justify-center rounded-full text-zinc-400 transition-colors hover:bg-white/10 hover:text-zinc-50"
                  >
                    <XIcon className="size-4" />
                  </button>
                </div>
              </div>
              {!minimized && confirmStage}
              {!minimized && dialStage}
            </>
          )}
        </FloatingCard>
      )}
    </AnimatePresence>
  );
}
